import { getCurrentLanguageId, removeClass, addClass } from "./common";
import DimensionSelector from "./dimension-selector";
import HttpClient from "./http-client";

class ProductVariantPage {
    ProductId: number;
    PageUrl: string;

    constructor(productId: number, pageUrl: string) {
        this.ProductId = productId;
        this.PageUrl = pageUrl;
    }
}

class SelectedDimensionValue{
    public dimensionNumber: number;
    public value: string;
}

export default class VariantSelector {
    private productDataElement: HTMLElement;
    private dimensionCount: number;
    private dimensionSelectors: DimensionSelector[];
    private loadProductPageFunction: (string) => Promise<void>;

    constructor(productDataElement: HTMLElement, loadProductPageFunction: (string) => Promise<void>) {
        this.productDataElement = productDataElement;
        this.loadProductPageFunction = loadProductPageFunction;
    }

    init(): void {
        const dimElements = Array.prototype.slice.call(this.productDataElement.querySelectorAll('[data-component-part="dim-value-list"]')).map(x => x as HTMLElement);

        this.dimensionCount = dimElements.length;
        this.dimensionSelectors = dimElements.map(e => new DimensionSelector(e, (n, v) => this.selectDimensionValue(n, v)));
    }

    async selectFirstVariant(): Promise<void> {
        await this.selectProductVariant(this.getProductId(), DimensionSelector[0]);
    }

    private async selectDimensionValue(dimensionNumber: number, value: string): Promise<void> {
        this.updateSelectedDimensionValue(dimensionNumber, value);

        const productId = this.getProductId();
        const selectedDimValues: SelectedDimensionValue[] = new Array(this.dimensionCount);

        for (let dimNumber = 1; dimNumber <= this.dimensionCount; dimNumber++) {
            if(dimNumber === dimensionNumber){
                selectedDimValues[dimNumber-1] = { dimensionNumber: dimensionNumber, value: value } as SelectedDimensionValue;
            }
            else {
                const dimValue = this.getSelectedDimensionValue(dimNumber);
                if(dimValue){
                    selectedDimValues[dimNumber-1] = { dimensionNumber: dimNumber, value: dimValue } as SelectedDimensionValue;
                }
            }
        }

        await this.selectProductVariant(productId, selectedDimValues);
    }

    private updateSelectedDimensionValue(dimensionNumber: number, value: string) {
        const selector = this.getDimensionSelector(dimensionNumber);
        selector.selectValue(value);
    }

    private async selectProductVariant(productId: number, dimValues: SelectedDimensionValue[]): Promise<void> {

        const langId = Number(getCurrentLanguageId());
        const productVariant = await this.resolveProductVariant(productId, langId, dimValues);

        if (!productVariant) {
            throw new Error('Product variant not resolved.');
        }

        if (productId === productVariant.ProductId) {
            return;
        }

        await this.loadProductPageFunction(productVariant.PageUrl);

    }

    private async resolveProductVariant(productId: number, langId: number, dimValues: SelectedDimensionValue[]): Promise<ProductVariantPage> {
        let url = `/api/product/resolve?productId=${productId}&langId=${langId}`;
        for (let dimNumber = 1; dimNumber <= this.dimensionCount; dimNumber++) {
            const dimValue = dimValues.find(x => x.dimensionNumber === dimNumber);
            if(dimValue){
                url += `&dim${dimValue.dimensionNumber}Value=${dimValue.value}`;
            }
        }
        const response = await new HttpClient().fetch(url);
        const json = await response.json();
        const productVariant = JSON.parse(json) as ProductVariantPage;
        return productVariant;
    }

    private getProductId(): number {
        return Number(this.productDataElement.getAttribute('data-product-id'));
    }

    private getSelectedDimensionValue(dimensionNumber: number): string {
        return this.productDataElement.getAttribute(`data-dim${dimensionNumber}`);
    }

    private getDimensionSelector(dimensionNumber: number){
        return this.dimensionSelectors.find(x => x.dimNumber === dimensionNumber);
    }
}
